"use strict";

exports.__esModule = true;
exports.IkConstraint = void 0;

var _BoneData = require("./BoneData");

var _Utils = require("./Utils");

/******************************************************************************
 * Spine Runtimes License Agreement
 * Last updated January 1, 2020. Replaces all prior versions.
 *
 * Copyright (c) 2013-2020, Esoteric Software LLC
 *
 * Integration of the Spine Runtimes into software or otherwise creating
 * derivative works of the Spine Runtimes is permitted under the terms and
 * conditions of Section 2 of the Spine Editor License Agreement:
 * http://esotericsoftware.com/spine-editor-license
 *
 * Otherwise, it is permitted to integrate the Spine Runtimes into software
 * or otherwise create derivative works of the Spine Runtimes (collectively,
 * "Products"), provided that each user of the Products must obtain their own
 * Spine Editor license and redistribution of the Products in any form must
 * include this license and copyright notice.
 *
 * THE SPINE RUNTIMES ARE PROVIDED BY ESOTERIC SOFTWARE LLC "AS IS" AND ANY
 * EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT LIMITED TO, THE IMPLIED
 * WARRANTIES OF MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE ARE
 * DISCLAIMED. IN NO EVENT SHALL ESOTERIC SOFTWARE LLC BE LIABLE FOR ANY
 * DIRECT, INDIRECT, INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES
 * (INCLUDING, BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES,
 * BUSINESS INTERRUPTION, OR LOSS OF USE, DATA, OR PROFITS) HOWEVER CAUSED AND
 * ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT LIABILITY, OR TORT
 * (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN ANY WAY OUT OF THE USE OF
 * THE SPINE RUNTIMES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGE.
 *****************************************************************************/

/** Stores the current pose for an IK constraint. An IK constraint adjusts the rotation of 1 or 2 constrained bones so the tip of
 * the last bone is as close to the target bone as possible.
 *
 * See [IK constraints](http://esotericsoftware.com/spine-ik-constraints) in the Spine User Guide. */
var IkConstraint = /*#__PURE__*/function () {
  function IkConstraint(data, skeleton) {
    /** The IK constraint's setup pose data. */
    this.data = null;
    /** The bones that will be modified by this IK constraint. */

    this.bones = null;
    /** The bone that is the IK target. */

    this.target = null;
    /** Controls the bend direction of the IK bones, either 1 or -1. */

    this.bendDirection = 0;
    /** When true and only a single bone is being constrained, if the target is too close, the bone is scaled to reach it. */

    this.compress = false;
    /** When true, if the target is out of range, the parent bone is scaled to reach it. If more than one bone is being constrained
     * and the parent bone has local nonuniform scale, stretch is not applied. */

    this.stretch = false;
    /** A percentage (0-1) that controls the mix between the constrained and unconstrained rotations. */

    this.mix = 1;
    /** For two bone IK, the distance from the maximum reach of the bones that rotation will slow. */

    this.softness = 0;
    this.active = false;
    if (!data) throw new Error("data cannot be null.");
    if (!skeleton) throw new Error("skeleton cannot be null.");
    this.data = data;
    this.mix = data.mix;
    this.softness = data.softness;
    this.bendDirection = data.bendDirection;
    this.compress = data.compress;
    this.stretch = data.stretch;
    this.bones = new Array();

    for (var i = 0; i < data.bones.length; i++) {
      this.bones.push(skeleton.findBone(data.bones[i].name));
    }

    this.target = skeleton.findBone(data.target.name);
  }

  var _proto = IkConstraint.prototype;

  _proto.isActive = function isActive() {
    return this.active;
  };

  _proto.update = function update() {
    if (this.mix == 0) return;
    var target = this.target;
    var bones = this.bones;

    switch (bones.length) {
      case 1:
        this.apply1(bones[0], target.worldX, target.worldY, this.compress, this.stretch, this.data.uniform, this.mix);
        break;

      case 2:
        this.apply2(bones[0], bones[1], target.worldX, target.worldY, this.bendDirection, this.stretch, this.data.uniform, this.softness, this.mix);
        break;
    }
  }
  /** Applies 1 bone IK. The target is specified in the world coordinate system. */
  ;

  _proto.apply1 = function apply1(bone, targetX, targetY, compress, stretch, uniform, alpha) {
    var p = bone.parent;
    var pa = p.a,
        pb = p.b,
        pc = p.c,
        pd = p.d;
    var rotationIK = -bone.ashearX - bone.arotation,
        tx = 0,
        ty = 0;

    switch (bone.data.transformMode) {
      case _BoneData.TransformMode.OnlyTranslation:
        tx = targetX - bone.worldX;
        ty = targetY - bone.worldY;
        break;

      case _BoneData.TransformMode.NoRotationOrReflection:
        var s = Math.abs(pa * pd - pb * pc) / (pa * pa + pc * pc);
        var sa = pa / bone.skeleton.scaleX;
        var sc = pc / bone.skeleton.scaleY;
        pb = -sc * s * bone.skeleton.scaleX;
        pd = sa * s * bone.skeleton.scaleY;
        rotationIK += Math.atan2(sc, sa) * _Utils.MathUtils.radDeg;
      // Fall through

      default:
        var x = targetX - p.worldX,
            y = targetY - p.worldY;
        var d = pa * pd - pb * pc;
        tx = (x * pd - y * pb) / d - bone.ax;
        ty = (y * pa - x * pc) / d - bone.ay;
    }

    rotationIK += Math.atan2(ty, tx) * _Utils.MathUtils.radDeg;
    if (bone.ascaleX < 0) rotationIK += 180;
    if (rotationIK > 180) rotationIK -= 360;else if (rotationIK < -180) rotationIK += 360;
    var sx = bone.ascaleX,
        sy = bone.ascaleY;

    if (compress || stretch) {
      switch (bone.data.transformMode) {
        case _BoneData.TransformMode.NoScale:
        case _BoneData.TransformMode.NoScaleOrReflection:
          tx = targetX - bone.worldX;
          ty = targetY - bone.worldY;
      }

      var b = bone.data.length * sx,
          dd = Math.sqrt(tx * tx + ty * ty);

      if (compress && dd < b || stretch && dd > b && b > 0.0001) {
        var _s = (dd / b - 1) * alpha + 1;

        sx *= _s;
        if (uniform) sy *= _s;
      }
    }

    bone.updateWorldTransformWith(bone.ax, bone.ay, bone.arotation + rotationIK * alpha, sx, sy, bone.ashearX, bone.ashearY);
  }
  /** Applies 2 bone IK. The target is specified in the world coordinate system.
   * @param child A direct descendant of the parent bone. */
  ;

  _proto.apply2 = function apply2(parent, child, targetX, targetY, bendDir, stretch, uniform, softness, alpha) {
    var px = parent.ax,
        py = parent.ay,
        psx = parent.ascaleX,
        psy = parent.ascaleY,
        sx = psx,
        sy = psy,
        csx = child.ascaleX;
    var os1 = 0,
        os2 = 0,
        s2 = 0;

    if (psx < 0) {
      psx = -psx;
      os1 = 180;
      s2 = -1;
    } else {
      os1 = 0;
      s2 = 1;
    }

    if (psy < 0) {
      psy = -psy;
      s2 = -s2;
    }

    if (csx < 0) {
      csx = -csx;
      os2 = 180;
    } else os2 = 0;

    var cx = child.ax,
        cy = 0,
        cwx = 0,
        cwy = 0,
        a = parent.a,
        b = parent.b,
        c = parent.c,
        d = parent.d;
    var u = Math.abs(psx - psy) <= 0.0001;

    if (!u || stretch) {
      cy = 0;
      cwx = a * cx + parent.worldX;
      cwy = c * cx + parent.worldY;
    } else {
      cy = child.ay;
      cwx = a * cx + b * cy + parent.worldX;
      cwy = c * cx + d * cy + parent.worldY;
    }

    var pp = parent.parent;
    a = pp.a;
    b = pp.b;
    c = pp.c;
    d = pp.d;
    var id = 1 / (a * d - b * c),
        x = cwx - pp.worldX,
        y = cwy - pp.worldY;
    var dx = (x * d - y * b) * id - px,
        dy = (y * a - x * c) * id - py;
    var l1 = Math.sqrt(dx * dx + dy * dy),
        l2 = child.data.length * csx,
        a1,
        a2;

    if (l1 < 0.0001) {
      this.apply1(parent, targetX, targetY, false, stretch, false, alpha);
      child.updateWorldTransformWith(cx, cy, 0, child.ascaleX, child.ascaleY, child.ashearX, child.ashearY);
      return;
    }

    x = targetX - pp.worldX;
    y = targetY - pp.worldY;
    var tx = (x * d - y * b) * id - px,
        ty = (y * a - x * c) * id - py;
    var dd = tx * tx + ty * ty;

    if (softness != 0) {
      softness *= psx * (csx + 1) * 0.5;
      var td = Math.sqrt(dd),
          sd = td - l1 - l2 * psx + softness;

      if (sd > 0) {
        var p = Math.min(1, sd / (softness * 2)) - 1;
        p = (sd - softness * (1 - p * p)) / td;
        tx -= p * tx;
        ty -= p * ty;
        dd = tx * tx + ty * ty;
      }
    }

    outer: if (u) {
      l2 *= psx;
      var cos = (dd - l1 * l1 - l2 * l2) / (2 * l1 * l2);

      if (cos < -1) {
        cos = -1;
        a2 = Math.PI * bendDir;
      } else if (cos > 1) {
        cos = 1;
        a2 = 0;

        if (stretch) {
          a = (Math.sqrt(dd) / (l1 + l2) - 1) * alpha + 1;
          sx *= a;
          if (uniform) sy *= a;
        }
      } else a2 = Math.acos(cos) * bendDir;

      a = l1 + l2 * cos;
      b = l2 * Math.sin(a2);
      a1 = Math.atan2(ty * a - tx * b, tx * a + ty * b);
    } else {
      a = psx * l2;
      b = psy * l2;
      var aa = a * a,
          bb = b * b,
          ta = Math.atan2(ty, tx);
      c = bb * l1 * l1 + aa * dd - aa * bb;
      var c1 = -2 * bb * l1,
          c2 = bb - aa;
      d = c1 * c1 - 4 * c2 * c;

      if (d >= 0) {
        var q = Math.sqrt(d);
        if (c1 < 0) q = -q;
        q = -(c1 + q) * 0.5;
        var r0 = q / c2,
            r1 = c / q;
        var r = Math.abs(r0) < Math.abs(r1) ? r0 : r1;

        if (r * r <= dd) {
          y = Math.sqrt(dd - r * r) * bendDir;
          a1 = ta - Math.atan2(y, r);
          a2 = Math.atan2(y / psy, (r - l1) / psx);
          break outer;
        }
      }

      var minAngle = _Utils.MathUtils.PI,
          minX = l1 - a,
          minDist = minX * minX,
          minY = 0;
      var maxAngle = 0,
          maxX = l1 + a,
          maxDist = maxX * maxX,
          maxY = 0;
      c = -a * l1 / (aa - bb);

      if (c >= -1 && c <= 1) {
        c = Math.acos(c);
        x = a * Math.cos(c) + l1;
        y = b * Math.sin(c);
        d = x * x + y * y;

        if (d < minDist) {
          minAngle = c;
          minDist = d;
          minX = x;
          minY = y;
        }

        if (d > maxDist) {
          maxAngle = c;
          maxDist = d;
          maxX = x;
          maxY = y;
        }
      }

      if (dd <= (minDist + maxDist) * 0.5) {
        a1 = ta - Math.atan2(minY * bendDir, minX);
        a2 = minAngle * bendDir;
      } else {
        a1 = ta - Math.atan2(maxY * bendDir, maxX);
        a2 = maxAngle * bendDir;
      }
    }

    var os = Math.atan2(cy, cx) * s2;
    var rotation = parent.arotation;
    a1 = (a1 - os) * _Utils.MathUtils.radDeg + os1 - rotation;
    if (a1 > 180) a1 -= 360;else if (a1 < -180) //
      a1 += 360;
    parent.updateWorldTransformWith(px, py, rotation + a1 * alpha, sx, sy, 0, 0);
    rotation = child.arotation;
    a2 = ((a2 + os) * _Utils.MathUtils.radDeg - child.ashearX) * s2 + os2 - rotation;
    if (a2 > 180) a2 -= 360;else if (a2 < -180) //
      a2 += 360;
    child.updateWorldTransformWith(cx, cy, rotation + a2 * alpha, child.ascaleX, child.ascaleY, child.ashearX, child.ashearY);
  };

  return IkConstraint;
}();

exports.IkConstraint = IkConstraint;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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